import * as React from "react"
import { getAttendanceSummary, getAttendanceByDate } from "../auth/api"
import Avatar from "./Avatar"

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"]

function pad(n) {
  return String(n).padStart(2, "0")
}

function toMonthString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}

function toDateString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export default function PresencasScreen({ onBack }) {
  const today = React.useMemo(() => new Date(), [])
  const [monthCursor, setMonthCursor] = React.useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  )
  const [selectedDate, setSelectedDate] = React.useState(toDateString(today))
  const [summary, setSummary] = React.useState({})
  const [attendance, setAttendance] = React.useState([])
  const [loadingSummary, setLoadingSummary] = React.useState(true)
  const [loadingAttendance, setLoadingAttendance] = React.useState(true)
  const [error, setError] = React.useState("")
  const [showMonthPicker, setShowMonthPicker] = React.useState(false)

  React.useEffect(() => {
    let mounted = true
    setLoadingSummary(true)
    getAttendanceSummary(toMonthString(monthCursor))
      .then((list) => {
        if (!mounted) return
        const map = {}
        list.forEach((item) => {
          map[item.data] = item.total
        })
        setSummary(map)
      })
      .catch((err) => {
        if (mounted) setError(err?.message || "Erro ao carregar calendário.")
      })
      .finally(() => {
        if (mounted) setLoadingSummary(false)
      })
    return () => {
      mounted = false
    }
  }, [monthCursor])

  React.useEffect(() => {
    let mounted = true
    setLoadingAttendance(true)
    getAttendanceByDate(selectedDate)
      .then((list) => {
        if (mounted) setAttendance(list)
      })
      .catch((err) => {
        if (mounted)
          setError(err?.message || "Erro ao carregar presenças do dia.")
      })
      .finally(() => {
        if (mounted) setLoadingAttendance(false)
      })
    return () => {
      mounted = false
    }
  }, [selectedDate])

  const daysGrid = React.useMemo(() => {
    const year = monthCursor.getFullYear()
    const month = monthCursor.getMonth()
    const firstWeekday = new Date(year, month, 1).getDay()
    const totalDays = new Date(year, month + 1, 0).getDate()

    const cells = []
    for (let i = 0; i < firstWeekday; i++) cells.push(null)
    for (let day = 1; day <= totalDays; day++) {
      cells.push(new Date(year, month, day))
    }
    return cells
  }, [monthCursor])

  function changeMonth(delta) {
    setMonthCursor(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1),
    )
  }

  function jumpToMonth(value) {
    // value vem como "YYYY-MM" do input type="month"
    const [year, month] = value.split("-").map(Number)
    if (year && month) {
      setMonthCursor(new Date(year, month - 1, 1))
    }
    setShowMonthPicker(false)
  }

  const monthLabel = monthCursor.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  })

  const selectedDateLabel = new Date(
    `${selectedDate}T00:00:00`,
  ).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })

  return (
    <div className="w-full max-w-3xl bg-slate-50 backdrop-blur rounded-2xl p-4 sm:p-6 shadow-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold break-words">
            Presenças
          </h1>
          <p className="text-slate-950 mt-1">
            Selecione um dia para ver os alunos presentes.
          </p>
        </div>
        <button
          onClick={onBack}
          className="shrink-0 self-start sm:self-auto text-sm px-4 py-2 rounded-lg border-2 border-cor-base text-cor-base font-semibold active:scale-[.94] active:duration-75 transition-all hover:scale-[1.05]"
          type="button">
          Voltar ao painel
        </button>
      </div>

      {error && <div className="text-red-700 mt-4">{error}</div>}

      {/* Navegação do mês */}
      <div className="mt-6 flex items-center justify-between relative">
        <button
          onClick={() => changeMonth(-1)}
          type="button"
          aria-label="Mês anterior"
          className="px-3 py-1.5 rounded-lg border-2 border-cor-base text-cor-base font-semibold hover:scale-[1.05] transition-all">
          ←
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMonthPicker((prev) => !prev)}
            className="font-semibold text-slate-900 capitalize px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-all">
            {monthLabel}
          </button>

          {showMonthPicker && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMonthPicker(false)}
              />
              <div className="absolute z-20 top-full mt-2 left-1/2 -translate-x-1/2 bg-white border-2 border-slate-200 rounded-xl p-3 shadow-2xl">
                <input
                  type="month"
                  autoFocus
                  defaultValue={toMonthString(monthCursor)}
                  onChange={(e) => jumpToMonth(e.target.value)}
                  className="border-2 border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-none focus:border-cor-base transition-colors"
                />
              </div>
            </>
          )}
        </div>

        <button
          onClick={() => changeMonth(1)}
          type="button"
          aria-label="Próximo mês"
          className="px-3 py-1.5 rounded-lg border-2 border-cor-base text-cor-base font-semibold hover:scale-[1.05] transition-all">
          →
        </button>
      </div>

      {/* Grade do calendário */}
      <div className="mt-4 grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, idx) => (
          <div key={idx} className="text-xs font-semibold text-slate-400 py-1">
            {w}
          </div>
        ))}
        {daysGrid.map((date, idx) => {
          if (!date) return <div key={idx} />
          const dateStr = toDateString(date)
          const hasAttendance = (summary[dateStr] || 0) > 0
          const isSelected = dateStr === selectedDate
          const isToday = dateStr === toDateString(today)

          return (
            <button
              key={idx}
              type="button"
              onClick={() => setSelectedDate(dateStr)}
              className={`relative aspect-square rounded-lg text-sm font-semibold flex items-center justify-center transition-all ${
                isSelected
                  ? "bg-cor-base text-slate-100"
                  : isToday
                    ? "border-2 border-cor-base text-cor-base"
                    : "text-slate-700 hover:bg-slate-200"
              }`}>
              {date.getDate()}
              {hasAttendance && (
                <span
                  className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${
                    isSelected ? "bg-slate-100" : "bg-cor-base"
                  }`}
                />
              )}
            </button>
          )
        })}
      </div>
      {loadingSummary && (
        <p className="text-xs text-slate-400 mt-2">Carregando calendário...</p>
      )}

      {/* Lista de presença do dia selecionado */}
      <div className="mt-6">
        <h2 className="font-semibold text-slate-900 capitalize">
          Presenças em {selectedDateLabel}
        </h2>

        {!loadingAttendance && (
          <p className="text-center mt-3 text-2xl font-semibold text-cor-base">
            {attendance.length}{" "}
            <span className="text-md font-normal text-slate-500">
              {attendance.length === 1 ? "Aluno presente" : "Alunos presentes"}
            </span>
          </p>
        )}

        <div className="mt-3 grid gap-2 max-h-[40vh] overflow-y-auto pr-1">
          {loadingAttendance ? (
            <p className="text-slate-500">Carregando...</p>
          ) : attendance.length === 0 ? (
            <p className="text-slate-500 text-center py-6">
              Nenhuma presença registrada nesse dia.
            </p>
          ) : (
            attendance.map((item) => (
              <div
                key={item.studentId}
                className="flex items-center justify-between gap-3 bg-white border-2 border-slate-100 rounded-xl p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar
                    src={item.fotoPerfilUrl}
                    size="md"
                    alt={item.nomeCompleto}
                    expandable
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 break-words">
                      {item.nomeCompleto}
                    </p>
                    <p className="text-xs text-slate-500">
                      Matrícula {item.matricula} · {item.graduacao}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-cor-base shrink-0">
                  {item.horario?.slice(0, 5)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
